'use client';

import React, { useEffect, type JSX } from 'react';

import MediumVariantA from '@components/ExternalWidgets/MediumVariantA';
import MediumVariantB from '@components/ExternalWidgets/MediumVariantB';
import MediumVariantCD from '@components/ExternalWidgets/MediumVariantCD';
import ProbabilityWidget from '@components/ExternalWidgets/StateProbability';
import { EventVo } from '@models/event';
import { pinEmbedPalette } from '../../../../lib/embedPalette';
import config from '@utils/config';
import { getEventRelativeUrl } from '@utils/events';

interface StateManagerProps {
    event: EventVo;
    variant: string;
    useDarkMode: boolean;
    utm_postfix: string;
    filterIds?: string[] | string;
    marketIds?: string;
    seriesTitle?: string;
}

const StateManager = ({
    event,
    variant,
    useDarkMode,
    utm_postfix,
    filterIds,
    marketIds,
    seriesTitle,
}: StateManagerProps): JSX.Element => {
    const goToMarket = () => {
        const externalUrl = `${config.baseUrl}${getEventRelativeUrl(
            event.series_ticker,
            seriesTitle,
            event.ticker,
        )}${utm_postfix}`;
        window.open(externalUrl, '_blank');
    };

    useEffect(() => {
        pinEmbedPalette(useDarkMode ? 'dark' : 'light');
    }, [useDarkMode]);

    if (variant === 'a') {
        let marketsToUse;

        if (marketIds) {
            // Use specifically selected markets in the provided order
            const selectedIds = marketIds.split(',');
            marketsToUse = selectedIds
                .map((id) => event.markets.find((m) => m.id === id))
                .filter((m): m is NonNullable<typeof m> => Boolean(m)); // Remove any not found
        } else {
            // Default to top 2 markets by price
            marketsToUse = [...event.markets].sort((a, b) => b.last_price - a.last_price).slice(0, 2);
        }

        // Stale/unmatched IDs -- an embed built before the gallery pruned selections, or IDs
        // from a different event -- filter down to nothing. Fall back to the top markets so the
        // watchlist still shows candidates instead of an empty list (existing embeds self-heal).
        if (marketsToUse.length === 0) {
            marketsToUse = [...event.markets].sort((a, b) => b.last_price - a.last_price).slice(0, 2);
        }

        return (
            <MediumVariantA
                markets={marketsToUse}
                eventName={event.title}
                eventTicker={event.ticker}
                seriesTicker={event.series_ticker}
                goToMarket={goToMarket}
                filterIds={filterIds}
                flexible
            />
        );
    }
    if (variant === 'b') {
        return (
            <MediumVariantB
                market1={event.markets[0]}
                market2={event.markets[1]}
                eventName={event.title}
                eventTicker={event.ticker}
                goToMarket={goToMarket}
            />
        );
    }

    // ONLY FOR ELECTION
    if (variant === 'c' || (variant === 'd' && event.ticker.includes('PRES-2024'))) {
        return (
            <MediumVariantCD
                isVariantC={variant === 'c'}
                market={event.markets[1]}
                eventName={event.title}
                eventTicker={event.ticker}
                goToMarket={goToMarket}
                darkMode={useDarkMode}
            />
        );
    }
    // ONLY FOR ELECTION... MAYBE CAN BE USED FOR OTHERS LATER??
    if (variant === 'e' && event.ticker.includes('PRESPARTY')) {
        return <ProbabilityWidget market={event.markets.at(0)} eventTicker={event.ticker} goToMarket={goToMarket} />;
    }
    return <></>;
};

export default StateManager;
